import { useEffect, useState } from 'react'
import {
  Table, Button, Space, Tag, message, Input, Select,
  Modal, Form, InputNumber, Switch, Popconfirm, Row, Col, Card, Upload, Image, Alert, Typography, Divider
} from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons'
import { productApi, categoryApi } from '../services/api'

interface Category {
  id: number
  name: string
  subCategories?: SubCategory[]
}

interface SubCategory {
  id: number
  name: string
  category_id: number
}

interface Product {
  id: number
  code: string
  name: string
  description?: string
  sub_category_id: number
  subCategory?: {
    id: number
    name: string
    category?: {
      id: number
      name: string
    }
  }
  enterprise_price: number
  personal_price: number
  unit: string
  stock: number
  status: number
  main_image?: string
  has_specs?: number
  specs?: SpecItem[]
}

interface SpecItem {
  description?: string
  image?: string
  name: string
  personal_price: number
  enterprise_price: number
  unit: string
  stock: number
  sku?: string
}

function Products() {
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [subCategories, setSubCategories] = useState<SubCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [filters, setFilters] = useState({ keyword: '', category_id: undefined as number | undefined, sub_category_id: undefined as number | undefined })
  const [modalOpen, setModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [form] = Form.useForm()
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [modalSubCategories, setModalSubCategories] = useState<SubCategory[]>([])
  const [imageFileList, setImageFileList] = useState<any[]>([])
  const [specs, setSpecs] = useState<SpecItem[]>([])

  // 加载分类
  useEffect(() => {
    categoryApi.getCategories().then((res: any) => {
      if (res.code === 200) {
        setCategories(res.data)
      }
    }).catch(() => {})
  }, [])

  // 切换大类时更新小类列表
  const handleCategoryChange = (catId: number | undefined) => {
    setFilters(f => ({ ...f, category_id: catId, sub_category_id: undefined }))
    if (catId) {
      const cat = categories.find(c => c.id === catId)
      setSubCategories(cat?.subCategories || [])
    } else {
      setSubCategories([])
    }
  }

  const fetchProducts = async (page = 1, pageSize = 20, extraFilters?: any) => {
    setLoading(true)
    const f = extraFilters || filters
    try {
      const res: any = await productApi.getProducts({
        page,
        page_size: pageSize,
        keyword: f.keyword || undefined,
        category_id: f.category_id || undefined,
        sub_category_id: f.sub_category_id || undefined,
        product_type: 1  // ❗ 只显示可售商品
      })
      if (res.code === 200) {
        setProducts(res.data.list)
        setPagination({ current: page, pageSize, total: res.data.total })
      }
    } catch {
      message.error('获取商品列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  const handleSearch = () => fetchProducts(1, pagination.pageSize)
  const handleReset = () => {
    const f = { keyword: '', category_id: undefined, sub_category_id: undefined }
    setFilters(f)
    setSubCategories([])
    fetchProducts(1, pagination.pageSize, f)
  }

  const handleAdd = () => {
    setEditingProduct(null)
    form.resetFields()
    setModalSubCategories([])
    setImageFileList([])
    setSpecs([])
    setModalOpen(true)
  }

  const handleEdit = (record: Product) => {
    setEditingProduct(record)
    if (record.subCategory?.category?.id) {
      const cat = categories.find(c => c.id === record.subCategory!.category!.id)
      setModalSubCategories(cat?.subCategories || [])
    }
    form.setFieldsValue({
      name: record.name,
      code: record.code,
      description: record.description,
      sub_category_id: record.sub_category_id,
      category_id: record.subCategory?.category?.id,
      enterprise_price: record.enterprise_price,
      personal_price: record.personal_price,
      unit: record.unit,
      stock: record.stock,
      status: record.status === 1,
      main_image: record.main_image
    })
    // Set image preview for existing product
    if (record.main_image) {
      setImageFileList([{
        uid: '-1',
        name: 'image.jpg',
        status: 'done',
        url: record.main_image.startsWith('http') ? record.main_image : record.main_image,
        thumbUrl: record.main_image.startsWith('http') ? record.main_image : record.main_image,
      }])
    } else {
      setImageFileList([])
    }
    // Load specs
    if (record.has_specs === 1 && record.specs) {
      const parsedSpecs = Array.isArray(record.specs) ? record.specs : (typeof record.specs === 'string' ? JSON.parse(record.specs) : [])
      setSpecs(parsedSpecs)
    } else {
      setSpecs([])
    }
    setModalOpen(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      const data = {
        ...values,
        status: values.status ? 1 : 0,
        has_specs: specs.length > 0 ? 1 : 0,
        specs: specs.length > 0 ? specs : null
      }
      delete data.category_id

      if (editingProduct) {
        await productApi.updateProduct(editingProduct.id, data)
        message.success('商品更新成功')
      } else {
        await productApi.createProduct(data)
        message.success('商品添加成功')
      }
      setModalOpen(false)
      fetchProducts(pagination.current, pagination.pageSize)
    } catch (err: any) {
      if (err?.errorFields) return
      message.error(editingProduct ? '更新失败' : '添加失败')
    }
  }

  const handleToggleStatus = async (record: Product) => {
    const newStatus = record.status === 1 ? 0 : 1
    try {
      await productApi.updateStatus(record.id, newStatus)
      message.success(newStatus === 1 ? '已上架' : '已下架')
      fetchProducts(pagination.current, pagination.pageSize)
    } catch {
      message.error('操作失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await productApi.deleteProduct(id)
      message.success('删除成功')
      fetchProducts(pagination.current, pagination.pageSize)
    } catch {
      message.error('删除失败')
    }
  }

  // 批量操作
  const handleBatchStatus = async (status: number) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择商品')
      return
    }
    try {
      await Promise.all(selectedRowKeys.map(id => productApi.updateStatus(id, status)))
      message.success(`已批量${status === 1 ? '上架' : '下架'} ${selectedRowKeys.length} 个商品`)
      setSelectedRowKeys([])
      fetchProducts(pagination.current, pagination.pageSize)
    } catch {
      message.error('操作失败')
    }
  }

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择商品')
      return
    }
    Modal.confirm({
      title: '批量删除确认',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个商品吗？`,
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await Promise.all(selectedRowKeys.map(id => productApi.deleteProduct(id)))
          message.success(`已删除 ${selectedRowKeys.length} 个商品`)
          setSelectedRowKeys([])
          fetchProducts(pagination.current, pagination.pageSize)
        } catch {
          message.error('删除失败')
        }
      }
    })
  }

  const handleModalCategoryChange = (catId: number) => {
    form.setFieldValue('sub_category_id', undefined)
    const cat = categories.find(c => c.id === catId)
    setModalSubCategories(cat?.subCategories || [])
  }

  const columns = [
    { title: '编码', dataIndex: 'code', key: 'code', width: 110 },
    {
      title: '图片',
      dataIndex: 'main_image',
      key: 'main_image',
      width: 70,
      render: (img: string) => img
        ? <Image src={img} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
        : <div style={{ width: 40, height: 40, background: '#f3f4f6', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 10 }}>无图</div>
    },
    { title: '商品名称', dataIndex: 'name', key: 'name', width: 140 },
    {
      title: '分类',
      key: 'category',
      width: 160,
      render: (_: any, record: Product) => {
        const cat = record.subCategory?.category?.name
        const sub = record.subCategory?.name
        return cat ? `${cat} / ${sub}` : '-'
      }
    },
    {
      title: '企业价',
      dataIndex: 'enterprise_price',
      key: 'enterprise_price',
      width: 90,
      render: (v: number) => `¥${Number(v).toFixed(2)}`
    },
    {
      title: '个人价',
      dataIndex: 'personal_price',
      key: 'personal_price',
      width: 90,
      render: (v: number) => `¥${Number(v).toFixed(2)}`
    },
    { title: '单位', dataIndex: 'unit', key: 'unit', width: 70 },
    {
      title: '库存',
      dataIndex: 'stock',
      key: 'stock',
      width: 80,
      render: (v: number) => (
        <span style={{ color: v < 10 ? '#ef4444' : '#10b981', fontWeight: 500 }}>{v}</span>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: number) => (
        <Tag color={v === 1 ? 'success' : 'default'}>{v === 1 ? '上架' : '下架'}</Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: Product) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleStatus(record)}
            style={{ color: record.status === 1 ? '#f59e0b' : '#10b981' }}
          >
            {record.status === 1 ? '下架' : '上架'}
          </Button>
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)} okText="确认" cancelText="取消">
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      {/* 搜索栏 */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <Row gutter={12} align="middle">
          <Col>
            <Input
              placeholder="搜索商品名称"
              prefix={<SearchOutlined />}
              value={filters.keyword}
              onChange={e => setFilters(f => ({ ...f, keyword: e.target.value }))}
              onPressEnter={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="选择大类"
              value={filters.category_id}
              onChange={handleCategoryChange}
              style={{ width: 140 }}
              allowClear
            >
              {categories.map(c => (
                <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="选择小类"
              value={filters.sub_category_id}
              onChange={v => setFilters(f => ({ ...f, sub_category_id: v }))}
              style={{ width: 140 }}
              allowClear
              disabled={!filters.category_id}
            >
              {subCategories.map(s => (
                <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} style={{ backgroundColor: '#55266c' }}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ backgroundColor: '#55266c' }}>
              添加商品
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 批量操作栏 */}
      {selectedRowKeys.length > 0 && (
        <Alert
          message={
            <Space>
              <span>已选择 {selectedRowKeys.length} 项</span>
              <Button size="small" onClick={() => handleBatchStatus(1)}>批量上架</Button>
              <Button size="small" onClick={() => handleBatchStatus(0)}>批量下架</Button>
              <Button size="small" danger onClick={handleBatchDelete}>批量删除</Button>
              <Button size="small" onClick={() => setSelectedRowKeys([])}>取消选择</Button>
            </Space>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as number[])
        }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 个商品`
        }}
        onChange={(p) => fetchProducts(p.current!, p.pageSize!)}
        size="middle"
        scroll={{ x: 1000 }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingProduct ? '编辑商品' : '添加商品'}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        width={600}
        okText="保存"
        cancelText="取消"
        okButtonProps={{ style: { backgroundColor: '#55266c' } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }]}>
                <Input placeholder="请输入商品名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="code" label="商品编码" rules={[{ required: true, message: '请输入商品编码' }]}>
                <Input placeholder="如: 0101001" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category_id" label="大类" rules={[{ required: true, message: '请选择大类' }]}>
                <Select placeholder="选择大类" onChange={handleModalCategoryChange}>
                  {categories.map(c => (
                    <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sub_category_id" label="小类" rules={[{ required: true, message: '请选择小类' }]}>
                <Select placeholder="选择小类">
                  {modalSubCategories.map(s => (
                    <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="enterprise_price" label="企业价(元)" rules={[{ required: true, message: '请输入企业价' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="personal_price" label="个人价(元)" rules={[{ required: true, message: '请输入个人价' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unit" label="单位" rules={[{ required: true, message: '请输入单位' }]}>
                <Input placeholder="斤/个/份" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="stock" label="库存数量">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="100" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="上架状态" valuePropName="checked" initialValue={true}>
                <Switch checkedChildren="上架" unCheckedChildren="下架" />
              </Form.Item>
            </Col>
          </Row>
                    <Divider style={{ margin: '12px 0' }}>多规格设置</Divider>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography.Text type="secondary">开启多规格后，每个规格都是独立的商品（有独立的名称/描述/图片/价格）</Typography.Text>
            <Switch checked={specs.length > 0} onChange={(checked) => {
              if (checked && specs.length === 0) {
                setSpecs([{ name: '', description: '', image: '', personal_price: 0, enterprise_price: 0, unit: '斤', stock: 0, sku: '' }])
              } else if (!checked) {
                setSpecs([])
              }
            }} />
          </div>
          {specs.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {specs.map((spec, idx) => (
                <div key={idx} style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 4, background: '#fafafa' }}>
                  <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography.Text strong>规格 {idx + 1}</Typography.Text>
                    <Button type="link" danger size="small" onClick={() => setSpecs(specs.filter((_, i) => i !== idx))}>删除此规格</Button>
                  </div>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item label="商品名称">
                        <Input placeholder="如：土豆丝" value={spec.name} onChange={e => { const s = [...specs]; s[idx].name = e.target.value; setSpecs(s) }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="商品描述">
                        <Input placeholder="如：新鲜土豆切丝" value={spec.description} onChange={e => { const s = [...specs]; s[idx].description = e.target.value; setSpecs(s) }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={8}>
                    <Col span={6}>
                      <Form.Item label="个人价">
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} value={spec.personal_price} onChange={v => { const s = [...specs]; s[idx].personal_price = v || 0; setSpecs(s) }} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="企业价">
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} value={spec.enterprise_price} onChange={v => { const s = [...specs]; s[idx].enterprise_price = v || 0; setSpecs(s) }} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item label="单位">
                        <Input placeholder="斤" value={spec.unit} onChange={e => { const s = [...specs]; s[idx].unit = e.target.value; setSpecs(s) }} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item label="库存">
                        <InputNumber min={0} style={{ width: '100%' }} value={spec.stock} onChange={v => { const s = [...specs]; s[idx].stock = v || 0; setSpecs(s) }} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item label="SKU编码">
                        <Input placeholder="可选" value={spec.sku || ''} onChange={e => { const s = [...specs]; s[idx].sku = e.target.value; setSpecs(s) }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="商品图片">
                    <Upload
                      name="file"
                      action="https://admin.zchef.cn/api/upload"
                      headers={{ Authorization: `Bearer ${localStorage.getItem('accessToken')}` }}
                      listType="picture-card"
                      maxCount={1}
                      fileList={spec.image ? [{ uid: `-spec-${idx}`, name: 'image', url: spec.image, status: 'done' }] : []}
                      onChange={(info) => {
                        if (info.file.status === 'done' && info.file.response?.data?.url) {
                          const s = [...specs]; s[idx].image = info.file.response.data.url; setSpecs(s)
                        } else if (info.file.status === 'removed') {
                          const s = [...specs]; s[idx].image = ''; setSpecs(s)
                        }
                      }}
                      onRemove={() => {
                        const s = [...specs]; s[idx].image = ''; setSpecs(s)
                      }}
                    >
                      {!spec.image && <div><UploadOutlined /><div style={{ marginTop: 4 }}>上传</div></div>}
                    </Upload>
                  </Form.Item>
                </div>
              ))}
              <Button type="dashed" block onClick={() => {
                setSpecs([...specs, { name: '', description: '', image: '', personal_price: 0, enterprise_price: 0, unit: '斤', stock: 0, sku: '' }]);
              }}>+ 添加规格</Button>
            </div>
          )}
          <Form.Item name="description" label="商品描述">
            <Input.TextArea rows={2} placeholder="商品描述（可选）" />
          </Form.Item>
          <Form.Item label="商品图片">
            <Upload
              name="file"
              action="https://admin.zchef.cn/api/upload"
              headers={{ Authorization: `Bearer ${localStorage.getItem('accessToken')}` }}
              listType="picture-card"
              maxCount={1}
              fileList={imageFileList}
              onChange={(info) => {
                // 用 info.fileList 驱动显示，处理所有状态
                const newFileList = info.fileList.map(f => {
                  if (f.status === 'done' && f.response?.data?.url) {
                    return { ...f, url: f.response.data.url, thumbUrl: f.response.data.url }
                  }
                  return f
                }).filter(f => f.status !== 'removed')
                setImageFileList(newFileList)

                if (info.file.status === 'done') {
                  const url = info.file.response?.data?.url
                  if (url) {
                    form.setFieldValue('main_image', url)
                  }
                  message.success('图片上传成功')
                } else if (info.file.status === 'removed') {
                  form.setFieldValue('main_image', undefined)
                } else if (info.file.status === 'error') {
                  message.error('图片上传失败')
                }
              }}
              onRemove={() => {
                form.setFieldValue('main_image', undefined)
              }}
            >
              {imageFileList.length === 0 && <div><UploadOutlined /><div style={{ marginTop: 4 }}>上传图片</div></div>}
            </Upload>
          </Form.Item>
          <Form.Item name="main_image" hidden><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Products
